// 소개서에 담을 블록 선택 — PDF 내보내기 / 브랜드 링크 두 팝업이 공유한다.
//
// 체크 전에 내용을 알 수 있어야 고를 수 있으므로 블록마다 한 줄 힌트를 붙인다.
// 내부 전용(매출·확정 딜)은 배지로 구분하고 기본값에서 빠져 있다 — 브랜드에 실수로
// 나가는 쪽보다 실수로 빠지는 쪽이 낫다.
import { BRAND_KIT_BLOCKS, type BrandKitBlockId } from './brandKitBlocks'
import type { ChannelSubs, Platform } from './creatorsApi'
import { PLATFORM_LABEL } from './sections/overview/format'

const CHANNELS: Platform[] = ['youtube', 'instagram', 'tiktok']

/** 채널 선택 — 협찬 콘텐츠·최근 협업이 이 채널들에서 뽑힌다.
 *  크리에이터가 안 가진 채널은 **목록에서 뺀다**. 고를 수는 있는데 늘 비는 항목이 되기 때문이다. */
export function BrandKitChannelPicker({
  value,
  onChange,
  channels,
}: {
  value: Platform[]
  onChange: (next: Platform[]) => void
  channels?: ChannelSubs[]
}) {
  const owned = CHANNELS.filter((p) => (channels ?? []).some((c) => c.platform === p))
  const list = owned.length > 0 ? owned : CHANNELS
  const selected = new Set(value)

  function toggle(p: Platform) {
    const next = new Set(selected)
    next.has(p) ? next.delete(p) : next.add(p)
    onChange(list.filter((x) => next.has(x)))
  }

  return (
    <div>
      <div className="mb-2 font-text text-[11px] font-medium tracking-[0.14em] text-fg-3">
        포함할 채널
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((p) => {
          const on = selected.has(p)
          return (
            <button
              key={p}
              type="button"
              onClick={() => toggle(p)}
              aria-pressed={on}
              className={[
                'border px-3 py-1.5 font-text text-[12px] transition-colors focus:shadow-focus focus:outline-none',
                on
                  ? 'border-brand-blue bg-brand-blue font-medium text-fg-inverse'
                  : 'border-line-soft bg-surface-base text-fg-3 hover:text-fg-1',
              ].join(' ')}
            >
              {PLATFORM_LABEL[p]}
            </button>
          )
        })}
      </div>
      {value.length === 0 && (
        <p className="mt-2 font-text text-[11px] text-status-danger">
          채널을 하나 이상 선택하세요.
        </p>
      )}
    </div>
  )
}

export function BrandKitBlockPicker({
  value,
  onChange,
  external,
}: {
  value: BrandKitBlockId[]
  onChange: (next: BrandKitBlockId[]) => void
  /** 외부 공유(브랜드 링크)인가 — 내부 전용 블록은 **목록에서 빼서** 고를 수조차 없게 한다.
   *  기본값에서만 빼면 실수로 켤 수 있고, 링크는 무비밀번호 공개라 되돌릴 수 없다. */
  external?: boolean
}) {
  const blocks = BRAND_KIT_BLOCKS.filter((b) => !(external && b.internalOnly))
  const selected = new Set(value)
  const selectable = blocks.filter((b) => !b.unavailable)
  const allOn = selectable.every((b) => selected.has(b.id))

  function toggle(id: BrandKitBlockId) {
    const next = new Set(selected)
    if (BRAND_KIT_BLOCKS.find((b) => b.id === id)?.unavailable) return
    next.has(id) ? next.delete(id) : next.add(id)
    // 목록 순서를 유지한다 — 선택 순서대로 담기면 문서 구성이 클릭 순서에 흔들린다.
    onChange(blocks.filter((b) => next.has(b.id)).map((b) => b.id))
  }

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="font-text text-[11px] font-medium tracking-[0.14em] text-fg-3">
          포함할 블록
        </span>
        <button
          type="button"
          onClick={() => onChange(allOn ? [] : selectable.map((b) => b.id))}
          className="font-text text-[12px] text-brand-blue hover:underline focus:shadow-focus focus:outline-none"
        >
          {allOn ? '전체 해제' : '전체 선택'}
        </button>
      </div>

      <div className="border border-line-hairline">
        {blocks.map((b, i) => (
          <label
            key={b.id}
            className={[
              'flex items-start gap-2.5 p-[11px_13px]',
              b.unavailable ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
              i < blocks.length - 1 ? 'border-b border-line-hairline' : '',
            ].join(' ')}
          >
            <input
              type="checkbox"
              checked={selected.has(b.id) && !b.unavailable}
              disabled={Boolean(b.unavailable)}
              onChange={() => toggle(b.id)}
              className="mt-[3px] h-3.5 w-3.5 flex-none accent-brand-blue focus:shadow-focus focus:outline-none"
            />
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="font-text text-[13px] font-medium text-fg-1">{b.label}</span>
                {b.unavailable && (
                  <span className="whitespace-nowrap border border-line-soft px-[5px] py-px font-text text-[10px] text-fg-3">
                    {b.unavailable}
                  </span>
                )}
                {b.internalOnly && (
                  <span className="whitespace-nowrap border border-brand-pink px-[5px] py-px font-text text-[10px] font-medium text-brand-pink">
                    내부 전용
                  </span>
                )}
              </span>
              <span className="mt-0.5 block break-keep font-text text-[11px] leading-[1.45] text-fg-3">
                {b.hint}
              </span>
            </span>
          </label>
        ))}
      </div>
    </div>
  )
}
